/**
 * resolveLook — flatten `theme_config.look` (preset_id + optional overrides)
 * into a fully concrete ResolvedLook bundle.
 *
 * Defensive: if `theme_config.look` is missing, malformed, or names an
 * unknown preset_id, the resolver falls back to the siteType's default preset
 * (or to `cozy_boutique` if no siteType is supplied). Unknown override values
 * for a knob are dropped (preset default wins for that knob).
 *
 * Used by:
 *   - `PublicLayout.tsx` to derive Header/Footer/Hero variants + CSS vars
 *   - `ThemeCustomizer.tsx` to populate the Look picker + Advanced UI
 *   - `db-setup.ts` (wizard) to seed `site_settings.theme_config.look`
 */

import { LOOKS, defaultPresetForSiteType, isPresetId } from './presets';
import { KNOB_VALUES } from './types';
import type { LookConfig, LookKnob, LookOverrides, PresetId, ResolvedLook } from './types';

function pick<K extends LookKnob>(knob: K, value: unknown, fallback: ResolvedLook[K]): ResolvedLook[K] {
  const allowed = KNOB_VALUES[knob] as readonly string[];
  return typeof value === 'string' && allowed.includes(value) ? (value as ResolvedLook[K]) : fallback;
}

/**
 * Resolve a Look from arbitrary JSON. Bad input → default preset, no throw.
 *
 * @param look       Raw `theme_config.look` value (may be undefined / malformed).
 * @param siteType   Optional siteType hint — when no preset is set, the
 *                   default for this siteType is used (falls back to
 *                   `cozy_boutique` if siteType is unknown).
 */
export function resolveLook(look: unknown, siteType?: string): ResolvedLook {
  const config = (look && typeof look === 'object' ? (look as Partial<LookConfig>) : {}) as Partial<LookConfig>;
  const presetId: PresetId = isPresetId(config.preset_id) ? config.preset_id : defaultPresetForSiteType(siteType);
  const preset = LOOKS[presetId];
  const overrides: LookOverrides =
    config.overrides && typeof config.overrides === 'object' ? config.overrides : {};

  // `search_forward` is a FUNCTIONAL hero — a photo with a dates/guests
  // availability search bar front and centre — not an aesthetic. It only makes
  // sense where there are properties to search. The `hospitality_warm` preset
  // carries it, and the wizard's look-picker rightly chooses that preset for
  // anything hospitality-flavoured, so a SaaS product *for* hosts (innscreen,
  // 2026-09-09) launched with a vacation-rental booking widget on its homepage.
  // Keep the Look; swap just the hero for the nearest photo-led variant. Only
  // when the caller says what the site is: an unknown siteType is left alone.
  let heroVariant = pick('hero_variant', overrides.hero_variant, preset.hero_variant);
  if (heroVariant === 'search_forward' && siteType !== undefined && siteType !== 'lodging') {
    heroVariant = 'fullscreen_media';
  }

  return {
    preset_id: presetId,
    header_variant: pick('header_variant', overrides.header_variant, preset.header_variant),
    hero_variant: heroVariant,
    footer_variant: pick('footer_variant', overrides.footer_variant, preset.footer_variant),
    button_shape: pick('button_shape', overrides.button_shape, preset.button_shape),
    card_style: pick('card_style', overrides.card_style, preset.card_style),
    radius_scale: pick('radius_scale', overrides.radius_scale, preset.radius_scale),
    density: pick('density', overrides.density, preset.density),
    font_pair_id: pick('font_pair_id', overrides.font_pair_id, preset.font_pair_id),
    palette_family_id: pick('palette_family_id', overrides.palette_family_id, preset.palette_family_id),
  };
}
